import React, { lazy, Suspense, useState } from 'react';
import { FileTextOutlined, ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useSelector } from 'react-redux';
import PopoverMenu from '../PopoverMenu/PopoverMenu';
import useDocumentCreation from '../../hooks/useDocumentCreation';
import CreateDocuments from '../CreateDocuments/CreateDocuments';

// Lazy load the OpenapiUpload component
const OpenapiUpload = lazy(() => import('../OpenapiUpload'));

/**
 * CreateButton component - A reusable button that provides create functionality
 * @param {Object} props - Component props
 * @param {boolean} props.onlyImport - Show only import button (no popover)
 * @param {boolean} props.onlyDocument - Show only new document button (no popover)
 * @param {string} props.folderId - Folder ID to create document in (optional)
 */
const CreateButton = ({ onlyImport = false, onlyDocument = false, folderId = null }) => {
  const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
  const { createDocument } = useDocumentCreation();

  // State for the import API modal
  const [importModalVisible, setImportModalVisible] = useState(false);

  // Function to open the import API modal
  const openImportModal = () => {
    setImportModalVisible(true);
  };

  // Function to close the import API modal
  const closeImportModal = () => {
    setImportModalVisible(false);
  };

  // Handle new document creation
  const handleNewDocument = () => {
    createDocument(folderId);
  };

  // Handle import action
  const handleImport = () => {
    openImportModal();
  };

  // If onlyImport is true, show only import button
  if (onlyImport) {
    return (
      <>
        <Button type="primary" icon={<ImportOutlined />} onClick={handleImport}>
          Import
        </Button>

        {/* Import API Modal */}
        {importModalVisible && (
          <Suspense fallback={null}>
            <OpenapiUpload
              folder_id={folderId}
              workspace_id={activeWorkspace?.id}
              open={importModalVisible}
              onCancel={closeImportModal}
            />
          </Suspense>
        )}
      </>
    );
  }

  // If onlyDocument is true, show only new document button
  if (onlyDocument) {
    return (
      <>
        <Button type="primary" icon={<FileTextOutlined />} onClick={handleNewDocument}>
          New document
        </Button>
        <CreateDocuments />
      </>
    );
  }

  // Default behavior - show both options in popover
  const createContent = [
    {
      element: (
        <Button type="button" htmlType="button" icon={<FileTextOutlined />}>
          New document
        </Button>
      ),
      clickHandler: handleNewDocument,
    },
    {
      element: (
        <Button type="button" htmlType="button" icon={<ImportOutlined />}>
          Import
        </Button>
      ),
      clickHandler: handleImport,
    },
  ];

  return (
    <>
      <PopoverMenu placement="bottom" arrow={false} content={createContent}>
        <Button type="primary" icon={<PlusOutlined />}>
          Create
        </Button>
      </PopoverMenu>

      <CreateDocuments />

      {/* Import API Modal */}
      {importModalVisible && (
        <Suspense fallback={null}>
          <OpenapiUpload
            folder_id={folderId}
            workspace_id={activeWorkspace?.id}
            open={importModalVisible}
            onCancel={closeImportModal}
          />
        </Suspense>
      )}
    </>
  );
};

export default CreateButton;
